import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"
import {
  GrowthBook,
  useFeatureValue,
  GrowthBookProvider as GrowthBookProviderWithoutInstance
} from "@growthbook/growthbook-react"
import type { FC, ReactNode } from "react"
import { useEffect, useState } from "react"
import { useConfigContext } from "~/providers/ConfigProvider"
import { useAnalytics, useCampAnonymousId } from "~/providers/AnalyticsProvider"
import { useLocation } from "react-router-dom"
import { SCREEN_BREAKPOINTS } from "~/hooks/useBreakpoint"

interface Props {
  children: ReactNode
}

/**
 * Coarse viewport class exposed to GrowthBook so experiments can be targeted to
 * a device type (e.g. mobile-only). Uses the same `md` breakpoint as the UI.
 */
const getDeviceType = (): "mobile" | "desktop" =>
  typeof window !== "undefined" && window.innerWidth < SCREEN_BREAKPOINTS.md
    ? "mobile"
    : "desktop"

const GrowthBookProvider: FC<Props> = ({ children }) => {
  const config = useConfigContext()

  const { track } = useAnalytics()
  const location = useLocation()
  const campAnonymousId = useCampAnonymousId()

  const [growthbook, setGrowthbook] = useState<typeof GrowthBook | undefined>(
    undefined
  )

  // Initialization.
  useEffect(() => {
    const run = async () => {
      const instance = new GrowthBook({
        apiHost: "https://cdn.growthbook.io",
        clientKey: config.growthBookClientKey,
        enableDevMode: true,
        attributes: {
          campAnonymousId,
          deviceType: getDeviceType()
        },
        trackingCallback: (
          experiment: { key: any },
          result: { variationId: any; stickyBucketUsed?: boolean }
        ) => {
          track(ANALYTICS_EVENTS.EXPERIMENT_VIEWED, {
            event_category: "experiment",
            experiment_id: experiment.key,
            variation_id: result.variationId,
            sticky_bucket_used: result.stickyBucketUsed ? "true" : "false",
            camp_anonymous_id: campAnonymousId
          })
        }
      })

      await instance.loadFeatures()

      setGrowthbook(instance)
    }
    run()
  }, [config, track, campAnonymousId])

  // Location change.
  useEffect(() => {
    if (!growthbook) return

    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      path: location.pathname,
      preview: new URLSearchParams(location.search).has("preview"),
      deviceType: getDeviceType()
    })
  }, [growthbook, location])

  return (
    <GrowthBookProviderWithoutInstance growthbook={growthbook}>
      {children}
    </GrowthBookProviderWithoutInstance>
  )
}

export const GrowthBookExperiment: FC<{
  experiment: string
  variation: string
  children: ReactNode
}> = ({ experiment, variation, children }) => {
  const currentVariationId = useFeatureValue(experiment, "")
  if (currentVariationId == variation) {
    return children
  } else {
    return null
  }
}

export const PossibleGrowthBookExperiment: FC<{
  experiment: string | null | undefined
  variation: string | null | undefined
  children?: ReactNode
}> = ({ experiment, variation, children }) => {
  if (experiment && variation) {
    return (
      <GrowthBookExperiment experiment={experiment} variation={variation}>
        {children}
      </GrowthBookExperiment>
    )
  } else {
    return <>{children}</>
  }
}

export default GrowthBookProvider
